import React, { useState, useEffect } from 'react';
import {
  Plus,
  Calendar,
  Tag,
  ShoppingBag,
  Store,
  X,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Types and interfaces
interface Discount {
  id: number;
  nombre_descuento: string;
  valor_descuento: number;
  fecha_inicio: string;
  fecha_fin: string;
  cant_min: number | null;
  cant_max: number | null;
  calculo_descuento: 'porcentaje' | 'monto_fijo' | 'unidades';
  tipo_descuento: string;
  activo: boolean;
  applications: DiscountApplication[];
  products: number[];
  categories: number[];
}

interface DiscountApplication {
  id: number;
  descuento_id: number;
  tipo: 'web' | 'mercadolibre' | 'local';
  local_id: number | null;
}

interface Product {
  id: number;
  nombre: string;
  categoria_id: number;
}

interface Category {
  id: number;
  nombre: string;
  path: string;
}

interface Local {
  id: number;
  nombre: string;
}

const Discounts: React.FC = () => {
  // States
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locals, setLocals] = useState<Local[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    nombre_descuento: '',
    applyTo: 'specific_products', // 'specific_products', 'categories', 'all_products'
    selectedProducts: [] as number[],
    selectedCategories: [] as number[],
    channels: {
      web: false,
      mercadolibre: false,
      locals: {} as Record<number, boolean>,
    },
    discountType: 'percentage', // 'percentage', 'fixed', 'min_quantity_percentage', 'min_quantity_fixed', 'offer_units'
    percentageValue: '',
    fixedValue: '',
    minQuantity: '',
    maxQuantity: '',
    offerUnits: '',
    pricedUnits: '',
    startDate: '',
    endDate: '',
    activo: true,
  });

  // Fetch data
  useEffect(() => {
    // Get user company ID first, then fetch data
    const getUserCompanyId = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        const { data, error } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', session.user.id)
          .single();

        if (!error && data) {
          setCompanyId(data.company_id);
          fetchDiscounts(data.company_id);
          fetchProducts(data.company_id);
          fetchCategories(data.company_id);
          fetchLocals(data.company_id);
        } else {
          console.error('Error fetching user company ID:', error);
          setError('Error al cargar los datos de usuario');
          setIsLoading(false);
        }
      } else {
        setError('No se encontró una sesión activa');
        setIsLoading(false);
      }
    };

    getUserCompanyId();
  }, []);

  const fetchDiscounts = async (company_id: string) => {
    try {
      setIsLoading(true);

      // Get discounts
      const { data: discountsData, error: discountsError } = await supabase
        .schema(company_id)
        .from('descuentos')
        .select('*');

      if (discountsError) throw discountsError;

      // Get applications for each discount
      const discountsWithApplications = await Promise.all(
        discountsData.map(async (discount) => {
          const { data: applications, error: applicationsError } =
            await supabase
              .schema(company_id)
              .from('descuentos_aplicaciones')
              .select('*')
              .eq('descuento_id', discount.id);

          if (applicationsError) throw applicationsError;

          // Get products for each discount
          const { data: products, error: productsError } = await supabase
            .schema(company_id)
            .from('descuentos_productos')
            .select('producto_id')
            .eq('descuento_id', discount.id);

          if (productsError) throw productsError;

          // Get categories for each discount
          const { data: categories, error: categoriesError } = await supabase
            .schema(company_id)
            .from('descuentos_categorias')
            .select('categoria_id')
            .eq('descuento_id', discount.id);

          if (categoriesError) throw categoriesError;

          return {
            ...discount,
            applications: applications || [],
            products: products?.map((p) => p.producto_id) || [],
            categories: categories?.map((c) => c.categoria_id) || [],
          };
        })
      );

      setDiscounts(discountsWithApplications);
    } catch (error) {
      console.error('Error fetching discounts:', error);
      setError('Error al cargar los descuentos');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async (company_id: string) => {
    try {
      const { data, error } = await supabase
        .schema(company_id)
        .from('productos')
        .select('id, nombre, categoria_id');

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async (company_id: string) => {
    try {
      const { data, error } = await supabase
        .schema(company_id)
        .from('categorias')
        .select('id, nombre, path');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLocals = async (company_id: string) => {
    try {
      const { data, error } = await supabase
        .schema(company_id)
        .from('locales')
        .select('id, nombre');

      if (error) throw error;
      setLocals(data || []);

      // Initialize locals in form state
      if (data) {
        const localsObj = data.reduce((acc, local) => {
          acc[local.id] = false;
          return acc;
        }, {} as Record<number, boolean>);

        setFormData((prev) => ({
          ...prev,
          channels: {
            ...prev.channels,
            locals: localsObj,
          },
        }));
      }
    } catch (error) {
      console.error('Error fetching locals:', error);
    }
  };

  // Open modal
  const handleOpenModal = () => {
    resetForm();
    setEditingDiscount(null);
    setIsModalOpen(true);
  };

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      nombre_descuento: '',
      applyTo: 'specific_products',
      selectedProducts: [],
      selectedCategories: [],
      channels: {
        web: false,
        mercadolibre: false,
        locals: locals.reduce((acc, local) => {
          acc[local.id] = false;
          return acc;
        }, {} as Record<number, boolean>),
      },
      discountType: 'percentage',
      percentageValue: '',
      fixedValue: '',
      minQuantity: '',
      maxQuantity: '',
      offerUnits: '',
      pricedUnits: '',
      startDate: '',
      endDate: '',
      activo: true,
    });
    setProductSearch('');
  };

  // Open edit modal
  const handleOpenEditModal = (discount: Discount) => {
    setEditingDiscount(discount);

    // Determine apply to option
    let applyTo = 'specific_products';
    if (discount.products.length === 0 && discount.categories.length > 0) {
      applyTo = 'categories';
    } else if (
      discount.products.length === 0 &&
      discount.categories.length === 0
    ) {
      applyTo = 'all_products';
    }

    // Determine discount type and values
    let discountType = 'percentage';
    let percentageValue = '';
    let fixedValue = '';
    let minQuantity = '';
    let maxQuantity = '';
    let offerUnits = '';
    let pricedUnits = '';

    if (discount.calculo_descuento === 'porcentaje') {
      discountType = 'percentage';
      percentageValue = discount.valor_descuento.toString();
    } else if (discount.calculo_descuento === 'monto_fijo') {
      if (discount.tipo_descuento === 'Monto directo') {
        discountType = 'fixed';
        fixedValue = discount.valor_descuento.toString();
      } else {
        discountType = 'min_quantity_fixed';
        fixedValue = discount.valor_descuento.toString();
        if (discount.cant_min) minQuantity = discount.cant_min.toString();
        if (discount.cant_max) maxQuantity = discount.cant_max.toString();
      }
    } else if (discount.calculo_descuento === 'unidades') {
      discountType = 'offer_units';
      if (discount.cant_min) {
        offerUnits = discount.cant_min.toString();
        // pricedUnits = offerUnits - valor_descuento
        pricedUnits = (discount.cant_min - discount.valor_descuento).toString();
      }
    }

    // Set min/max quantities if applicable
    if (
      discountType === 'min_quantity_percentage' ||
      discountType === 'min_quantity_fixed'
    ) {
      minQuantity = discount.cant_min ? discount.cant_min.toString() : '1';
      maxQuantity = discount.cant_max ? discount.cant_max.toString() : '';
    }

    // Set up channels
    const web = discount.applications.some((app) => app.tipo === 'web');
    const mercadolibre = discount.applications.some(
      (app) => app.tipo === 'mercadolibre'
    );

    // Set up locals
    const localsObj = locals.reduce((acc, local) => {
      acc[local.id] = discount.applications.some(
        (app) => app.tipo === 'local' && app.local_id === local.id
      );
      return acc;
    }, {} as Record<number, boolean>);

    // Format dates in YYYY-MM-DD for input
    const startDate = discount.fecha_inicio.split('T')[0];
    const endDate = discount.fecha_fin.split('T')[0];

    // Set form data
    setFormData({
      nombre_descuento: discount.nombre_descuento,
      applyTo,
      selectedProducts: discount.products,
      selectedCategories: discount.categories,
      discountType,
      percentageValue,
      fixedValue,
      minQuantity,
      maxQuantity,
      offerUnits,
      pricedUnits,
      startDate,
      endDate,
      activo: discount.activo,
      channels: {
        web,
        mercadolibre,
        locals: localsObj,
      },
    });

    setIsModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    resetForm();
    setEditingDiscount(null);
    setIsModalOpen(false);
    setError(null);
  };

  // Delete discount
  const handleDeleteDiscount = async () => {
    if (!editingDiscount || !companyId) return;

    try {
      setIsLoading(true);

      // Delete discount applications
      const { error: deleteAppsError } = await supabase
        .schema(companyId)
        .from('descuentos_aplicaciones')
        .delete()
        .eq('descuento_id', editingDiscount.id);

      if (deleteAppsError) throw deleteAppsError;

      // Delete product associations
      const { error: deleteProductsError } = await supabase
        .schema(companyId)
        .from('descuentos_productos')
        .delete()
        .eq('descuento_id', editingDiscount.id);

      if (deleteProductsError) throw deleteProductsError;

      // Delete category associations
      const { error: deleteCategoriesError } = await supabase
        .schema(companyId)
        .from('descuentos_categorias')
        .delete()
        .eq('descuento_id', editingDiscount.id);

      if (deleteCategoriesError) throw deleteCategoriesError;

      // Delete the discount itself
      const { error: deleteDiscountError } = await supabase
        .schema(companyId)
        .from('descuentos')
        .delete()
        .eq('id', editingDiscount.id);

      if (deleteDiscountError) throw deleteDiscountError;

      // Refresh discounts list and close modals
      await fetchDiscounts(companyId);
      setShowDeleteConfirm(false);
      handleCloseModal();
    } catch (error) {
      console.error('Error deleting discount:', error);
      setError('Error al eliminar el descuento');
    } finally {
      setIsLoading(false);
    }
  };

  // Input change handlers
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    setFormData({
      ...formData,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  // Handle checkbox changes for channels
  const handleChannelChange = (
    channel: string,
    value: boolean,
    localId?: number
  ) => {
    if (channel === 'web' || channel === 'mercadolibre') {
      setFormData({
        ...formData,
        channels: {
          ...formData.channels,
          [channel]: value,
        },
      });
    } else if (channel === 'local' && localId !== undefined) {
      setFormData({
        ...formData,
        channels: {
          ...formData.channels,
          locals: {
            ...formData.channels.locals,
            [localId]: value,
          },
        },
      });
    }
  };

  // Handle product selection
  const handleProductSelection = (productId: number, isSelected: boolean) => {
    if (isSelected) {
      setFormData({
        ...formData,
        selectedProducts: [...formData.selectedProducts, productId],
      });
    } else {
      setFormData({
        ...formData,
        selectedProducts: formData.selectedProducts.filter(
          (id) => id !== productId
        ),
      });
    }
  };

  // Get formatted category name with full path
  const getFormattedCategoryName = (category: Category): string => {
    if (!category.path) return category.nombre;

    // Extract category IDs from path (format: /1/2/3)
    const pathIds = category.path.split('/').filter((id) => id !== '');

    // Build hierarchical name
    const pathNames = pathIds.map((id) => {
      const categoryId = parseInt(id);
      const pathCategory = categories.find((cat) => cat.id === categoryId);
      return pathCategory ? pathCategory.nombre : '';
    });

    return pathNames.join(' > ');
  };

  // Check if a category is a child of another
  const isCategoryChild = (
    possibleChildId: number,
    possibleParentId: number
  ): boolean => {
    const possibleChild = categories.find((cat) => cat.id === possibleChildId);
    if (!possibleChild || !possibleChild.path) return false;

    // Check if the parent ID is in the child's path
    return possibleChild.path.includes(`/${possibleParentId}/`);
  };

  // Get all child category IDs for a given category
  const getChildCategories = (categoryId: number): number[] => {
    return categories
      .filter((category) => isCategoryChild(category.id, categoryId))
      .map((category) => category.id);
  };

  // Handle category selection
  const handleCategorySelection = (categoryId: number, isSelected: boolean) => {
    if (isSelected) {
      // Get all child categories
      const childCategoryIds = getChildCategories(categoryId);

      // Add the selected category and all its children
      setFormData({
        ...formData,
        selectedCategories: [
          ...formData.selectedCategories.filter(
            (id) => id !== categoryId && !childCategoryIds.includes(id)
          ),
          categoryId,
          ...childCategoryIds,
        ],
      });
    } else {
      // If deselecting, remove this category and all its children
      const childCategoryIds = getChildCategories(categoryId);

      setFormData({
        ...formData,
        selectedCategories: formData.selectedCategories.filter(
          (id) => id !== categoryId && !childCategoryIds.includes(id)
        ),
      });
    }
  };

  // Handle product search
  const handleProductSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductSearch(e.target.value);
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId) {
      setError('Error: No se pudo determinar la empresa del usuario');
      return;
    }

    try {
      setIsLoading(true);

      // Calculate discount value and type
      let valorDescuento = 0;
      let calculoDescuento: 'porcentaje' | 'monto_fijo' | 'unidades' = 'porcentaje';
      let tipoDescuento = '';
      let cantMin: number | null = null;
      let cantMax: number | null = null;

      if (formData.discountType === 'percentage') {
        valorDescuento = parseFloat(formData.percentageValue);
        calculoDescuento = 'porcentaje';
        tipoDescuento = 'Simple';
      } else if (formData.discountType === 'fixed') {
        valorDescuento = parseFloat(formData.fixedValue);
        calculoDescuento = 'monto_fijo';
        tipoDescuento = 'Monto directo';
      } else if (formData.discountType === 'min_quantity_percentage') {
        valorDescuento = parseFloat(formData.percentageValue);
        calculoDescuento = 'porcentaje';
        tipoDescuento = 'Por cantidad mínima, porcentaje';
      } else if (formData.discountType === 'min_quantity_fixed') {
        valorDescuento = parseFloat(formData.fixedValue);
        calculoDescuento = 'monto_fijo';
        tipoDescuento = 'Por cantidad mínima, monto fijo';
      } else if (formData.discountType === 'offer_units') {
        // Calculate the discount based on the "buy X get Y" formula
        const offerUnits = parseInt(formData.offerUnits);
        const pricedUnits = parseInt(formData.pricedUnits);

        if (offerUnits > 0 && pricedUnits > 0) {
          // Set both min and max quantity to the same value (offerUnits)
          // to ensure the discount applies only when exactly that many items are purchased
          cantMin = offerUnits;
          cantMax = offerUnits;

          // Store the units to be discounted (offerUnits - pricedUnits)
          // Example: If buying 3 for price of 2, discount is 1 unit
          valorDescuento = offerUnits - pricedUnits;
          calculoDescuento = 'unidades';
          tipoDescuento = 'Por cantidad precisa';
        }
      }

      // Set min/max quantities if applicable
      if (
        formData.discountType === 'min_quantity_percentage' ||
        formData.discountType === 'min_quantity_fixed'
      ) {
        cantMin = formData.minQuantity ? parseInt(formData.minQuantity) : 1;
        cantMax = formData.maxQuantity ? parseInt(formData.maxQuantity) : null;
      }

      // Create the discount data object
      const discountData = {
        nombre_descuento: formData.nombre_descuento,
        valor_descuento: valorDescuento,
        fecha_inicio: formData.startDate,
        fecha_fin: formData.endDate,
        cant_min: cantMin,
        cant_max: cantMax,
        calculo_descuento: calculoDescuento,
        tipo_descuento: tipoDescuento,
        activo: formData.activo,
      };

      let discountId: number;

      if (editingDiscount) {
        // Update existing discount
        const { error: discountError } = await supabase
          .schema(companyId)
          .from('descuentos')
          .update(discountData)
          .eq('id', editingDiscount.id);

        if (discountError) throw discountError;
        discountId = editingDiscount.id;

        // Delete existing applications
        const { error: deleteAppsError } = await supabase
          .schema(companyId)
          .from('descuentos_aplicaciones')
          .delete()
          .eq('descuento_id', discountId);

        if (deleteAppsError) throw deleteAppsError;

        // Delete existing product associations
        const { error: deleteProductsError } = await supabase
          .schema(companyId)
          .from('descuentos_productos')
          .delete()
          .eq('descuento_id', discountId);

        if (deleteProductsError) throw deleteProductsError;

        // Delete existing category associations
        const { error: deleteCategoriesError } = await supabase
          .schema(companyId)
          .from('descuentos_categorias')
          .delete()
          .eq('descuento_id', discountId);

        if (deleteCategoriesError) throw deleteCategoriesError;
      } else {
        // Insert new discount
        const { data: newDiscount, error: discountError } = await supabase
          .schema(companyId)
          .from('descuentos')
          .insert([discountData])
          .select('id')
          .single();

        if (discountError) throw discountError;
        if (!newDiscount) throw new Error('No se pudo crear el descuento');

        discountId = newDiscount.id;
      }

      // Create applications
      const applications: Omit<DiscountApplication, 'id'>[] = [];

      // Web
      if (formData.channels.web) {
        applications.push({
          descuento_id: discountId,
          tipo: 'web',
          local_id: null,
        });
      }

      // MercadoLibre
      if (formData.channels.mercadolibre) {
        applications.push({
          descuento_id: discountId,
          tipo: 'mercadolibre',
          local_id: null,
        });
      }

      // Locals
      Object.entries(formData.channels.locals).forEach(
        ([localId, isSelected]) => {
          if (isSelected) {
            applications.push({
              descuento_id: discountId,
              tipo: 'local',
              local_id: parseInt(localId),
            });
          }
        }
      );

      // Insert applications
      if (applications.length > 0) {
        const { error: applicationsError } = await supabase
          .schema(companyId)
          .from('descuentos_aplicaciones')
          .insert(applications);

        if (applicationsError) throw applicationsError;
      }

      // Assign to products if applicable
      if (
        formData.applyTo === 'specific_products' &&
        formData.selectedProducts.length > 0
      ) {
        const productDiscounts = formData.selectedProducts.map((productId) => ({
          descuento_id: discountId,
          producto_id: productId,
        }));

        const { error: productsError } = await supabase
          .schema(companyId)
          .from('descuentos_productos')
          .insert(productDiscounts);

        if (productsError) throw productsError;
      } else if (
        formData.applyTo === 'categories' &&
        formData.selectedCategories.length > 0
      ) {
        const categoryDiscounts = formData.selectedCategories.map(
          (categoryId) => ({
            descuento_id: discountId,
            categoria_id: categoryId,
          })
        );

        const { error: categoriesError } = await supabase
          .schema(companyId)
          .from('descuentos_categorias')
          .insert(categoryDiscounts);

        if (categoriesError) throw categoriesError;
      } else if (formData.applyTo === 'all_products') {
        // Apply to all categories
        const allCategoryDiscounts = categories.map((category) => ({
          descuento_id: discountId,
          categoria_id: category.id,
        }));

        const { error: allCategoriesError } = await supabase
          .schema(companyId)
          .from('descuentos_categorias')
          .insert(allCategoryDiscounts);

        if (allCategoriesError) throw allCategoriesError;
      }

      // Refresh discounts and close modal
      await fetchDiscounts(companyId);
      handleCloseModal();
    } catch (error) {
      console.error('Error saving discount:', error);
      setError('Error al guardar el descuento');
    } finally {
      setIsLoading(false);
    }
  };

  // Card displaying a discount
  const DiscountCard = ({ discount }: { discount: Discount }) => {
    const now = new Date();
    const startDate = new Date(discount.fecha_inicio);
    const endDate = new Date(discount.fecha_fin);

    // Determine status of the discount
    let status: 'active' | 'upcoming' | 'finished' | 'disabled' = 'disabled';

    if (discount.activo) {
      if (now >= startDate && now <= endDate) {
        status = 'active';
      } else if (now < startDate) {
        status = 'upcoming';
      } else if (now > endDate) {
        status = 'finished';
      }
    }

    // Set status label and styles based on status
    const statusConfig = {
      active: {
        label: 'Activo',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-500',
      },
      upcoming: {
        label: 'Próximamente',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-500',
      },
      finished: {
        label: 'Finalizado',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        borderColor: 'border-gray-300',
      },
      disabled: {
        label: 'Desactivado',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-gray-300',
      },
    };

    const { label, bgColor, textColor, borderColor } = statusConfig[status];

    return (
      <div
        className={`bg-white rounded-lg shadow-sm p-6 border-l-4 ${borderColor}`}
      >
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-semibold text-gray-900">
            {discount.nombre_descuento}
          </h3>
          <div className="flex items-center space-x-2">
            <span
              className={`px-2 py-1 text-xs rounded-full ${bgColor} ${textColor}`}
            >
              {label}
            </span>
            <button
              onClick={() => handleOpenEditModal(discount)}
              className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50"
              title="Editar descuento"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center text-sm text-gray-600">
          <Calendar className="w-4 h-4 mr-2" />
          <span>
            {format(new Date(discount.fecha_inicio), 'dd MMM yyyy', {
              locale: es,
            })}{' '}
            -
            {format(new Date(discount.fecha_fin), 'dd MMM yyyy', {
              locale: es,
            })}
          </span>
        </div>

        <div className="mt-2 text-sm text-gray-600">
          <div className="flex items-center">
            <Tag className="w-4 h-4 mr-2" />
            <span>
              {discount.calculo_descuento === 'porcentaje'
                ? `${discount.valor_descuento}% de descuento`
                : discount.calculo_descuento === 'monto_fijo'
                ? `$${discount.valor_descuento} de descuento`
                : discount.cant_min
                ? `${discount.cant_min} unidades al precio de ${discount.cant_min - discount.valor_descuento}`
                : `${discount.valor_descuento} unidades de descuento`}
            </span>
          </div>

          {(discount.cant_min || discount.cant_max) && discount.calculo_descuento !== 'unidades' && (
            <div className="flex items-center mt-1">
              <ShoppingBag className="w-4 h-4 mr-2" />
              <span>
                {discount.cant_min && `Mínimo: ${discount.cant_min} unidades`}
                {discount.cant_min && discount.cant_max && ' - '}
                {discount.cant_max && `Máximo: ${discount.cant_max} unidades`}
              </span>
            </div>
          )}

          {discount.applications.length > 0 && (
            <div className="flex items-center mt-1">
              <Store className="w-4 h-4 mr-2" />
              <span>
                Canales:{' '}
                {discount.applications
                  .map((app) => {
                    if (app.tipo === 'web') return 'Tienda web';
                    if (app.tipo === 'mercadolibre') return 'MercadoLibre';
                    if (app.tipo === 'local') {
                      const local = locals.find((l) => l.id === app.local_id);
                      return local ? local.nombre : 'Local';
                    }
                    return '';
                  })
                  .join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Tag className="w-8 h-8 text-gray-700" />
          <h1 className="text-2xl font-semibold text-gray-900">Descuentos</h1>
        </div>
        <button
          onClick={handleOpenModal}
          className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-normal rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
            />
          </svg>
          Agregar Descuento
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <>
          {error && (
            <div className="bg-red-100 text-red-800 p-4 rounded-md mb-6">
              {error}
            </div>
          )}

          {discounts.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-600 mb-4">
                No hay descuentos configurados
              </p>
              <button
                onClick={handleOpenModal}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md inline-flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  />
                </svg>
                Crear tu primer descuento
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {discounts.map((discount) => (
                <DiscountCard key={discount.id} discount={discount} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal for adding/editing discounts */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto relative">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingDiscount ? 'Editar Descuento' : 'Agregar Descuento'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 p-6">
                {/* Nombre del descuento */}
                <div>
                  <label
                    htmlFor="nombre_descuento"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nombre del descuento
                  </label>
                  <input
                    type="text"
                    id="nombre_descuento"
                    name="nombre_descuento"
                    value={formData.nombre_descuento}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    placeholder="Ej: Descuento Verano"
                  />
                </div>

                {/* Aplicar a */}
                <div>
                  <label
                    htmlFor="applyTo"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Aplicar a
                  </label>
                  <select
                    id="applyTo"
                    name="applyTo"
                    value={formData.applyTo}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <option value="specific_products">
                      Productos específicos
                    </option>
                    <option value="categories">Categorías completas</option>
                    <option value="all_products">Todos los productos</option>
                  </select>
                </div>

                {/* Productos (cuando se selecciona Productos específicos) */}
                {formData.applyTo === 'specific_products' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Seleccionar productos
                    </label>
                    <div className="border border-gray-300 rounded-md">
                      <div className="sticky top-0 bg-white p-3 border-b border-gray-200">
                        <input
                          type="search"
                          value={productSearch}
                          onChange={handleProductSearch}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          placeholder="Buscar productos"
                        />
                      </div>
                      <div className="p-3 max-h-48 overflow-y-auto">
                        <div>
                          {products.length === 0 ? (
                            <p className="text-gray-500 text-sm">
                              No hay productos disponibles
                            </p>
                          ) : (
                            products
                              .filter((product) =>
                                product.nombre
                                  .toLowerCase()
                                  .includes(productSearch.toLowerCase())
                              )
                              .map((product) => (
                                <div
                                  key={product.id}
                                  className="flex items-center mb-2 last:mb-0"
                                >
                                  <input
                                    type="checkbox"
                                    id={`product-${product.id}`}
                                    checked={formData.selectedProducts.includes(
                                      product.id
                                    )}
                                    onChange={(e) =>
                                      handleProductSelection(
                                        product.id,
                                        e.target.checked
                                      )
                                    }
                                    className="mr-2"
                                  />
                                  <label
                                    htmlFor={`product-${product.id}`}
                                    className="text-sm text-gray-700"
                                  >
                                    {product.nombre}
                                  </label>
                                </div>
                              ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Categorías (cuando se selecciona Categorías completas) */}
                {formData.applyTo === 'categories' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Seleccionar categorías
                    </label>
                    <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                      {categories.length === 0 ? (
                        <p className="text-gray-500 text-sm">
                          No hay categorías disponibles
                        </p>
                      ) : (
                        categories
                          .slice() // Create a copy to avoid mutating the original array
                          .sort((a, b) =>
                            getFormattedCategoryName(a).localeCompare(
                              getFormattedCategoryName(b)
                            )
                          )
                          .map((category) => (
                            <div
                              key={category.id}
                              className="flex items-center mb-2 last:mb-0"
                            >
                              <input
                                type="checkbox"
                                id={`category-${category.id}`}
                                checked={formData.selectedCategories.includes(
                                  category.id
                                )}
                                onChange={(e) =>
                                  handleCategorySelection(
                                    category.id,
                                    e.target.checked
                                  )
                                }
                                className="mr-2"
                              />
                              <label
                                htmlFor={`category-${category.id}`}
                                className="text-sm"
                              >
                                {getFormattedCategoryName(category)}
                              </label>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                )}

                {/* Canales */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Canal/es
                  </label>
                  <div className="space-y-4">
                    <div className="border border-gray-300 rounded-md p-3">
                      <h4 className="font-medium text-sm mb-2">
                        Tiendas online
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="web"
                            checked={formData.channels.web}
                            onChange={(e) =>
                              handleChannelChange('web', e.target.checked)
                            }
                            className="mr-2"
                          />
                          <label htmlFor="web" className="text-sm">
                            Tiendanube
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="mercadolibre"
                            checked={formData.channels.mercadolibre}
                            onChange={(e) =>
                              handleChannelChange(
                                'mercadolibre',
                                e.target.checked
                              )
                            }
                            className="mr-2"
                          />
                          <label htmlFor="mercadolibre" className="text-sm">
                            MercadoLibre
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-300 rounded-md p-3">
                      <h4 className="font-medium text-sm mb-2">
                        Tiendas físicas
                      </h4>
                      <div className="space-y-2">
                        {locals.length === 0 ? (
                          <p className="text-gray-500 text-sm">
                            No hay locales disponibles
                          </p>
                        ) : (
                          locals.map((local) => (
                            <div key={local.id} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`local-${local.id}`}
                                checked={!!formData.channels.locals[local.id]}
                                onChange={(e) =>
                                  handleChannelChange(
                                    'local',
                                    e.target.checked,
                                    local.id
                                  )
                                }
                                className="mr-2"
                              />
                              <label
                                htmlFor={`local-${local.id}`}
                                className="text-sm"
                              >
                                {local.nombre}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tipo de descuento */}
                <div>
                  <label
                    htmlFor="discountType"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Tipo de descuento
                  </label>
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm text-gray-600">
                      Simples
                    </h4>
                    <div className="space-y-2 ml-2">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="percentage"
                          name="discountType"
                          value="percentage"
                          checked={formData.discountType === 'percentage'}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <label htmlFor="percentage" className="text-sm">
                          Porcentaje directo
                        </label>
                      </div>
                      {formData.discountType === 'percentage' && (
                        <div className="ml-6">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            id="percentageValue"
                            name="percentageValue"
                            value={formData.percentageValue}
                            onChange={handleInputChange}
                            required
                            className="w-24 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            placeholder="Ej: 15"
                          />
                          <span className="ml-2">%</span>
                        </div>
                      )}

                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="fixed"
                          name="discountType"
                          value="fixed"
                          checked={formData.discountType === 'fixed'}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <label htmlFor="fixed" className="text-sm">
                          Monto directo
                        </label>
                      </div>
                      {formData.discountType === 'fixed' && (
                        <div className="ml-6">
                          <span className="mr-2">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            id="fixedValue"
                            name="fixedValue"
                            value={formData.fixedValue}
                            onChange={handleInputChange}
                            required
                            className="w-32 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            placeholder="Ej: 1000"
                          />
                        </div>
                      )}
                    </div>

                    <h4 className="font-medium text-sm text-gray-600 mt-4">
                      Avanzados
                    </h4>
                    <div className="space-y-2 ml-2">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="min_quantity_percentage"
                          name="discountType"
                          value="min_quantity_percentage"
                          checked={
                            formData.discountType === 'min_quantity_percentage'
                          }
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <label
                          htmlFor="min_quantity_percentage"
                          className="text-sm"
                        >
                          Por cantidad mínima, descuento porcentual
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 ml-6">
                        Ejemplo: Llevando más de 6 unidades, te hacemos un
                        descuento del 20%
                      </p>
                      {formData.discountType === 'min_quantity_percentage' && (
                        <div className="ml-6 space-y-2">
                          <div>
                            <label
                              htmlFor="minQuantity"
                              className="text-xs text-gray-500 mr-2"
                            >
                              Cantidad mínima:
                            </label>
                            <input
                              type="number"
                              min="1"
                              id="minQuantity"
                              name="minQuantity"
                              value={formData.minQuantity}
                              onChange={handleInputChange}
                              required
                              className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="maxQuantity"
                              className="text-xs text-gray-500 mr-2"
                            >
                              Cantidad máxima (opcional):
                            </label>
                            <input
                              type="number"
                              min={formData.minQuantity || 1}
                              id="maxQuantity"
                              name="maxQuantity"
                              value={formData.maxQuantity}
                              onChange={handleInputChange}
                              className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="percentageValue"
                              className="text-xs text-gray-500 mr-2"
                            >
                              Porcentaje:
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              id="percentageValue"
                              name="percentageValue"
                              value={formData.percentageValue}
                              onChange={handleInputChange}
                              required
                              className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            />
                            <span className="ml-1">%</span>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="min_quantity_fixed"
                          name="discountType"
                          value="min_quantity_fixed"
                          checked={
                            formData.discountType === 'min_quantity_fixed'
                          }
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <label htmlFor="min_quantity_fixed" className="text-sm">
                          Por cantidad mínima, monto fijo
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 ml-6">
                        Ejemplo: Llevando más de 4 unidades, te descontamos
                        $4000
                      </p>
                      {formData.discountType === 'min_quantity_fixed' && (
                        <div className="ml-6 space-y-2">
                          <div>
                            <label
                              htmlFor="minQuantity"
                              className="text-xs text-gray-500 mr-2"
                            >
                              Cantidad mínima:
                            </label>
                            <input
                              type="number"
                              min="1"
                              id="minQuantity"
                              name="minQuantity"
                              value={formData.minQuantity}
                              onChange={handleInputChange}
                              required
                              className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="maxQuantity"
                              className="text-xs text-gray-500 mr-2"
                            >
                              Cantidad máxima (opcional):
                            </label>
                            <input
                              type="number"
                              min={formData.minQuantity || 1}
                              id="maxQuantity"
                              name="maxQuantity"
                              value={formData.maxQuantity}
                              onChange={handleInputChange}
                              className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="fixedValue"
                              className="text-xs text-gray-500 mr-2"
                            >
                              Monto fijo:
                            </label>
                            <span className="mr-1">$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              id="fixedValue"
                              name="fixedValue"
                              value={formData.fixedValue}
                              onChange={handleInputChange}
                              required
                              className="w-32 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="offer_units"
                          name="discountType"
                          value="offer_units"
                          checked={formData.discountType === 'offer_units'}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <label htmlFor="offer_units" className="text-sm">
                          Por cantidad precisa
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 ml-6">
                        Ejemplo: Promoción 2x1
                      </p>
                      {formData.discountType === 'offer_units' && (
                        <div className="ml-6 space-y-2">
                          <div className="flex items-center">
                            <label
                              htmlFor="offerUnits"
                              className="text-xs text-gray-500 mr-2"
                            >
                              Ofreces
                            </label>
                            <input
                              type="number"
                              min="1"
                              id="offerUnits"
                              name="offerUnits"
                              value={formData.offerUnits}
                              onChange={handleInputChange}
                              required
                              className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            />
                            <span className="mx-2 text-xs text-gray-500">
                              unidades
                            </span>
                          </div>
                          <div className="flex items-center">
                            <label
                              htmlFor="pricedUnits"
                              className="text-xs text-gray-500 mr-2"
                            >
                              por el precio de
                            </label>
                            <input
                              type="number"
                              min="1"
                              id="pricedUnits"
                              name="pricedUnits"
                              value={formData.pricedUnits}
                              onChange={handleInputChange}
                              required
                              className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            />
                            <span className="mx-2 text-xs text-gray-500">
                              unidades
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fechas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="startDate"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Fecha de inicio
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="endDate"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Fecha de fin
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      required
                      min={formData.startDate}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Botones */}
                <div className="flex justify-between items-center mt-8">
                  {editingDiscount && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="inline-flex items-center px-4 py-2 border border-red-600 text-red-600 bg-white hover:bg-red-50 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      disabled={isLoading}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0111 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        />
                      </svg>
                      Eliminar Descuento
                    </button>
                  )}

                  <div className={`${editingDiscount ? '' : 'ml-auto'}`}>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="mr-3 inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 font-normal rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-normal rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      {isLoading
                        ? 'Guardando...'
                        : editingDiscount
                        ? 'Actualizar Descuento'
                        : 'Crear Descuento'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-md w-full max-w-md p-6 shadow-xl">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Eliminar Descuento
            </h2>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro que deseas eliminar el descuento "
              {editingDiscount?.nombre_descuento}"? Esta acción no se puede
              deshacer.
            </p>
            <div className="flex justify-end items-center gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-normal rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteDiscount}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-normal rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { Discounts };
